/**
 * Kanban Page
 *
 * Página principal de negociações (Kanban/Lista).
 * Lógica em useKanbanPage; funnelStages vem do hook (sem useLeads duplicado).
 */

import React from 'react';
import { useKanbanPage } from './hooks/useKanbanPage';
import OpportunityDetailsPanel from './components/OpportunityDetailsPanel';
import OpportunityFormSidebar from './components/OpportunityFormSidebar';
import ContactFormSheet from '../../components/shared/ContactFormSheet';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import KanbanToolbar from './components/KanbanToolbar';
import KanbanBoard from './components/KanbanBoard';
import LeadListView from './components/LeadListView';
import PipelineSummaryBar from './components/PipelineSummaryBar';
import BulkActionsBar from '../../components/shared/BulkActionsBar';
import { usePipelineSummary } from './hooks/usePipelineSummary';
import { MOBILE_CONTENT_BOTTOM_PAD_CLASS } from '../../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';

const Kanban: React.FC = () => {
  const {
    // State
    viewMode,
    setViewMode,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    filteredLeads,
    leadsByStage,
    selectedLead,
    isOpportunitySidebarOpen,
    isDeleteModalOpen,
    leadToDelete,
    isContactModalOpen,
    contacts,
    funnels,
    funnelStages,
    selectedFunnelId,
    users,

    // Actions
    handleCardClick,
    handleClosePanel,
    handleSaveLeadAndClosePanel,
    handleOpenCreateModal,
    handleSaveLeadFromModal,
    closeFormModal,
    handleOpenDeleteModal,
    confirmDeleteLead,
    closeDeleteModal,
    setSelectedFunnelId,
    handleCreateFunnel,
    handleEditFunnel,
    handleOpenContactModal,
    handleCloseContactModal,
    handleSaveContactFromModal,
    createdContactForDeal,
    clearCreatedContactForDeal,
    handleChatClick,
    handleRefresh,

    // Page Specific
    handleQuickFilterChange,
    handleStageFilterChange,
    handleDuplicateLead,

    // List pagination
    currentPage,
    setCurrentPage,
    pageSize,
    paginatedLeads,
    groupByOrigin,
    setGroupByOrigin,

    // Sub-hooks
    selection,
    bulk
  } = useKanbanPage();

  const pipelineSummary = usePipelineSummary(filteredLeads, funnelStages);

  return (
    <main
      aria-label="Negociações"
      className="flex h-full min-w-0 flex-col overflow-x-hidden bg-surface-1 md:bg-background md:pt-s4"
    >
      {/* Chrome: no mobile, bloco único separado do board */}
      <header className="min-w-0 shrink-0 border-b border-border bg-surface-0 px-s4 pb-s3 pt-s3 md:mb-s2 md:border-0 md:bg-transparent md:px-s6 md:pb-0 md:pt-0">
        <KanbanToolbar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onFilterChange={setFilters}
          onQuickFilterChange={handleQuickFilterChange}
          onStageFilterChange={handleStageFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onCreateLead={handleOpenCreateModal}
          funnels={funnels}
          selectedFunnelId={selectedFunnelId}
          onFunnelChange={setSelectedFunnelId}
          onEditFunnel={handleEditFunnel}
          onCreateFunnel={handleCreateFunnel}
          currentFilters={filters}
          funnelStages={funnelStages}
        />
      </header>

      <PipelineSummaryBar summary={pipelineSummary} />

      {viewMode === 'kanban' ? (
        <section aria-label="Quadro kanban" className="min-h-0 flex-1 overflow-hidden">
          <KanbanBoard
            leadsByStage={leadsByStage}
            onCardClick={handleCardClick}
            onCreateLead={handleOpenCreateModal}
            onChatClick={handleChatClick}
            contacts={contacts}
            users={users}
          />
        </section>
      ) : (
        <section
          aria-label="Lista de negociações"
          className={cn('min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-s4 pt-s4 md:px-s6 md:pt-s6 md:pb-s6', MOBILE_CONTENT_BOTTOM_PAD_CLASS)}
        >
          <LeadListView
            leads={paginatedLeads}
            onLeadClick={handleCardClick}
            onDelete={handleOpenDeleteModal}
            onDuplicate={handleDuplicateLead}
            showCheckbox={true}
            selectedIds={selection.selectedIds}
            allSelected={selection.isAllSelected}
            someSelected={selection.isSomeSelected}
            onToggleSelection={selection.toggleSelection}
            onToggleAll={selection.toggleAll}
            groupByOrigin={groupByOrigin}
            onGroupByOriginChange={setGroupByOrigin}
            pagination={{
              currentPage,
              totalCount: filteredLeads.length,
              pageSize,
              onPageChange: setCurrentPage,
              selectedCount: selection.selectedIds.size,
            }}
          />
        </section>
      )}

      {viewMode === 'list' && (
        <BulkActionsBar
          selectedCount={selection.selectedIds.size}
          actions={bulk.bulkActions}
          onClose={selection.clearSelection}
        />
      )}

      <OpportunityDetailsPanel
        lead={selectedLead}
        onClose={handleClosePanel}
        onSave={handleSaveLeadAndClosePanel}
        onDelete={handleOpenDeleteModal}
      />

      <OpportunityFormSidebar
        isOpen={isOpportunitySidebarOpen}
        onClose={closeFormModal}
        onSave={handleSaveLeadFromModal}
        onOpenContactModal={handleOpenContactModal}
        contacts={contacts}
        users={users}
        createdContact={createdContactForDeal}
        onCreatedContactApplied={clearCreatedContactForDeal}
      />

      <ContactFormSheet
        isOpen={isContactModalOpen}
        onClose={handleCloseContactModal}
        onSave={handleSaveContactFromModal}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteLead}
        title="Excluir Lead"
        message={`Tem certeza que deseja excluir o lead "${leadToDelete?.name}"? Esta ação não pode ser desfeita.`}
      />

      <ConfirmationModal
        isOpen={bulk.isBulkDeleteModalOpen}
        onClose={() => bulk.setBulkDeleteModalOpen(false)}
        onConfirm={bulk.handleBulkDelete}
        title="Excluir Negociações"
        message={`Tem certeza que deseja excluir ${selection.selectedIds.size} negociação(ões)? Esta ação não pode ser desfeita.`}
        confirmButtonText="Excluir"
        confirmButtonColor="red"
      />
    </main>
  );
};

export default Kanban;
